import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card,
  CardContent,
  CardMedia,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  WaterDrop,
  Biotech,
  Engineering
} from '@mui/icons-material';
import { useLanguage } from '../../context/LanguageContext';

// Styled components
const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: 'relative',
}));

const TechnologyCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
  }
}));

const IconBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '70px',
  height: '70px',
  borderRadius: '50%',
  backgroundColor: 'rgba(14, 165, 233, 0.1)',
  marginBottom: theme.spacing(2),
}));

export default function PatentTechnologies() {
  // eslint-disable-next-line no-unused-vars
  const { language } = useLanguage();
  
  // Rewolute system features
  const rewoluteFeatures = [
    { title: 'HIGHEST RECOVERY', description: '97% Recovery with Single Skid Reverse Osmosis', color: '#003087', textColor: 'white', icon: '🔄' },
    { title: '15-20% LESSER CAPITAL COST', description: 'Reduced investment compared to conventional systems', color: '#003087', textColor: 'white', icon: '💰' },
    { title: 'LOWEST POWER CONSUMPTION', description: 'Energy-efficient operation for sustainable treatment', color: '#8CC63F', textColor: 'black', icon: '⚡' },
    { title: 'FIRST IN CLASS TECHNOLOGY', description: 'Cutting-edge innovation in water treatment', color: '#8CC63F', textColor: 'black', icon: '🔬' },
    { title: 'EASY TO MONITOR', description: 'Simple operation and maintenance requirements', color: '#003087', textColor: 'white', icon: '📊' },
    { title: 'COMPACT & AESTHETIC', description: 'Space-saving design with attractive appearance', color: '#003087', textColor: 'white', icon: '✨' },
    { title: 'REDUCES RMS LOAD', description: 'Lower maintenance and operational burden', color: '#8CC63F', textColor: 'black', icon: '🔧' },
    { title: 'LESS FOOTPRINT', description: 'Minimal space requirements for installation', color: '#8CC63F', textColor: 'black', icon: '📏' },
    { title: 'COMPLETE AUTOMATION', description: 'Fully automated operation for consistent performance', color: '#003087', textColor: 'white', icon: '🤖' },
    { title: '120-140 GPL RO REJECT', description: 'Efficient handling of reject water', color: '#003087', textColor: 'white', icon: '💧' },
  ];

  return (
    <Box sx={{
      position: 'relative',
      overflow: 'hidden',
      bgcolor: 'white'
    }}>

      {/* Rewolute Technology Section */}
      <Section sx={{ 
        position: 'relative', 
        overflow: 'hidden', 
        pt: 8, 
        pb: 10,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9f7fe 100%)'
      }}>
        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(140,198,63,0.1) 0%, rgba(140,198,63,0) 70%)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        <Box sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(0,48,135,0.05) 0%, rgba(0,48,135,0) 70%)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '-20px',
                    top: '10px',
                    bottom: '10px',
                    width: '4px',
                    background: 'linear-gradient(180deg, #003087 0%, #8CC63F 100%)',
                    borderRadius: '4px'
                  }
                }}>
                  <Typography 
                    variant="overline" 
                    sx={{ 
                      color: '#003087', 
                      fontWeight: 700,
                      letterSpacing: 3,
                      mb: 1,
                      display: 'block',
                      fontSize: '1rem',
                      pl: 1
                    }}
                  >
                    INNOVATIVE TECHNOLOGY
                  </Typography>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      fontWeight: 800, 
                      mb: 2,
                      color: '#003087',
                      fontSize: { xs: '2.5rem', md: '4rem' },
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      lineHeight: 1.1,
                      pl: 1
                    }}
                  >
                    REWOLUTE
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  mt: 4, 
                  p: 3, 
                  bgcolor: 'rgba(255,255,255,0.7)', 
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
                }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#8CC63F',
                      mb: 2,
                      fontWeight: 700,
                      fontSize: { xs: '1.3rem', md: '1.6rem' }
                    }}
                  >
                    97% RECOVERY WITH SINGLE SKID REVERSE OSMOSIS
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#555',
                      fontSize: '1.1rem',
                      lineHeight: 1.8
                    }}
                  >
                    Advanced Stream of Effluent treatment with Novel Membrane Technology to facilitate
                    Highest possible recovery Using Single Skid Reverse Osmosis system
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            
            {/* Right Content - Logo */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
              >
                <Box sx={{
                  position: 'relative',
                  p: 4,
                  bgcolor: 'white',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '8px',
                    background: 'linear-gradient(90deg, #003087 0%, #8CC63F 100%)'
                  }
                }}>
                  <Box 
                    component="img"
                    src="/images/revolute/re.png"
                    alt="REWOLUTE"
                    sx={{ 
                      width: '100%', 
                      height: 'auto',
                      filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))'
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Section>
      
      {/* System Diagram Section */}
      <Section sx={{ 
        py: 10, 
        bgcolor: 'transparent',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(0,48,135,0.1) 50%, transparent 100%)'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(0,48,135,0.1) 50%, transparent 100%)'
        }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  color: '#003087',
                  textAlign: 'center',
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                System Performance
              </Typography>
              
              <Box 
                sx={{ 
                  width: '80px', 
                  height: '4px', 
                  bgcolor: '#8CC63F', 
                  mx: 'auto',
                  mb: 4,
                  borderRadius: '2px'
                }} 
              />
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#555',
                  maxWidth: '700px',
                  mx: 'auto',
                  fontSize: '1.1rem',
                  mb: 8
                }}
              >
                The REWOLUTE system achieves industry-leading performance with over 97% recovery rate
                and minimal brine output, making it the most efficient solution available.
              </Typography>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                whileHover={{ y: -5, transition: { duration: 0.3 } }}
              >
                <Box sx={{ 
                  maxWidth: '900px', 
                  mx: 'auto', 
                  p: { xs: 3, md: 6 }, 
                  bgcolor: 'transparent',
                  borderRadius: '20px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Background pattern */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0.02,
                    backgroundImage: 'radial-gradient(#003087 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }} />
                  
                  <Box sx={{ position: 'relative', width: '100%', height: '250px' }}>
                    {/* Input */}
                    <Box sx={{ 
                      position: 'absolute', 
                      left: 0, 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Box sx={{
                        bgcolor: '#003087',
                        color: 'white',
                        py: 1.5,
                        px: 3,
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 16px rgba(0,48,135,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          RO Feed
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '80px', 
                        height: '8px', 
                        bgcolor: '#003087', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        ml: 1
                      }}>
                        <Box sx={{ 
                          width: 0, 
                          height: 0, 
                          borderTop: '10px solid transparent',
                          borderBottom: '10px solid transparent',
                          borderLeft: '16px solid #003087'
                        }} />
                      </Box>
                    </Box>
                    
                    {/* REWOLUTE Box */}
                    <Box sx={{ 
                      position: 'absolute', 
                      left: '50%', 
                      top: '50%', 
                      transform: 'translate(-50%, -50%)',
                      width: '280px',
                      height: '120px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2
                    }}>
                      <Box 
                        component="img"
                        src="/images/revolute/re-1.png"
                        alt="REWOLUTE"
                        sx={{ width: '220px', height: 'auto' }}
                      />
                    </Box>
                    
                    {/* Output - Permeate */}
                    <Box sx={{ 
                      position: 'absolute', 
                      left: '50%', 
                      bottom: 0, 
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Box sx={{ 
                        width: '8px', 
                        height: '60px', 
                        bgcolor: '#8CC63F', 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                      }}>
                        <Box sx={{ 
                          width: 0, 
                          height: 0, 
                          borderLeft: '10px solid transparent',
                          borderRight: '10px solid transparent',
                          borderTop: '16px solid #8CC63F'
                        }} />
                      </Box>
                      <Box sx={{
                        bgcolor: '#8CC63F',
                        color: 'black',
                        py: 1.5,
                        px: 3,
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        mt: 1,
                        boxShadow: '0 8px 16px rgba(140,198,63,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {'>'}97% Permeate
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Output - Brine */}
                    <Box sx={{ 
                      position: 'absolute', 
                      right: 0, 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Box sx={{ 
                        width: '80px', 
                        height: '8px', 
                        bgcolor: '#EA4335', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                      }}>
                        <Box sx={{ 
                          width: 0, 
                          height: 0, 
                          borderTop: '10px solid transparent',
                          borderBottom: '10px solid transparent',
                          borderLeft: '16px solid #EA4335'
                        }} />
                      </Box>
                      <Box sx={{
                        bgcolor: '#EA4335',
                        color: 'white',
                        py: 1.5,
                        px: 3,
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        ml: 1,
                        boxShadow: '0 8px 16px rgba(234,67,53,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {'<'}3% Brine
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </motion.div>
          </Box>
        </Container>
      </Section>
      
      {/* Features Section */}
      {/* Features Section */}
      <Section sx={{ 
        py: 10, 
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #f0f7ff 100%)'
      }}>
        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '20%',
          right: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0,48,135,0.03) 0%, rgba(0,48,135,0) 70%)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        <Box sx={{ 
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(140,198,63,0.03) 0%, rgba(140,198,63,0) 70%)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  color: '#003087',
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                KEY FEATURES
              </Typography>
              
              <Box 
                sx={{ 
                  width: '80px', 
                  height: '4px', 
                  bgcolor: '#8CC63F', 
                  mx: 'auto',
                  mb: 4,
                  borderRadius: '2px'
                }} 
              />
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#555',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 6
                }}
              >
                Our revolutionary REWOLUTE technology offers unparalleled advantages over conventional systems,
                delivering superior performance with industry-leading efficiency
              </Typography>
            </motion.div>
          </Box>
          
          {/* Features grid layout */}
          <Grid container spacing={3} justifyContent="center">
            {[
              { title: 'HIGHEST RECOVERY', description: '97% Recovery with Single Skid Reverse Osmosis', color: '#003087', icon: '🔄' },
              { title: '15-20% LESSER CAPITAL COST', description: 'Reduced investment compared to conventional systems', color: '#003087', icon: '💰' },
              { title: 'LOWEST POWER CONSUMPTION', description: 'Energy-efficient operation for sustainable treatment', color: '#8CC63F', icon: '⚡' },
              { title: 'FIRST IN CLASS TECHNOLOGY', description: 'Cutting-edge innovation in water treatment', color: '#8CC63F', icon: '🔬' },
              { title: 'EASY TO MONITOR', description: 'Simple operation and maintenance requirements', color: '#003087', icon: '📊' },
              { title: 'COMPACT & AESTHETIC', description: 'Space-saving design with attractive appearance', color: '#003087', icon: '✨' },
              { title: 'REDUCES RMS LOAD', description: 'Lower maintenance and operational burden', color: '#8CC63F', icon: '🔧' },
              { title: 'LESS FOOTPRINT', description: 'Minimal space requirements for installation', color: '#8CC63F', icon: '📏' },
              { title: 'COMPLETE AUTOMATION', description: 'Fully automated operation for consistent performance', color: '#003087', icon: '🤖' },
              { title: '120-140 GPL RO REJECT', description: 'Efficient handling of reject water', color: '#003087', icon: '💧' },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + (index * 0.05) }}
                  whileHover={{ 
                    y: -5, 
                    transition: { duration: 0.2 } 
                  }}
                >
                  <Box sx={{ 
                    bgcolor: 'white',
                    p: 3,
                    borderRadius: '10px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.03)',
                    border: `1px solid ${feature.color === '#003087' ? 'rgba(0,48,135,0.1)' : 'rgba(140,198,63,0.15)'}`,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      boxShadow: '0 15px 30px rgba(0,0,0,0.08)',
                      transform: 'translateY(-5px)'
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: feature.color,
                    }
                  }}>
                    {/* Icon circle */}
                    <Box sx={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      bgcolor: feature.color === '#003087' ? 'rgba(0,48,135,0.08)' : 'rgba(140,198,63,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mt: 1
                    }}>
                      <Typography 
                        variant="h2" 
                        sx={{ 
                          fontSize: '1.6rem',
                          color: feature.color
                        }}
                      >
                        {feature.icon}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 1.5,
                        color: feature.color,
                        fontSize: '0.85rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {feature.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        fontSize: '0.8rem',
                        lineHeight: 1.6,
                        flexGrow: 1
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>
      
      {/* REWOLUTE RO Systems Section */}
      <Section sx={{ 
        py: 10, 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #f0f7ff 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle, rgba(0,48,135,0.02) 0%, rgba(0,48,135,0) 70%)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  color: '#003087',
                  textAlign: 'center'
                }}
              >
                REWOLUTE RO Systems
              </Typography>
              
              <Box 
                sx={{ 
                  width: '80px', 
                  height: '4px', 
                  bgcolor: '#8CC63F', 
                  mx: 'auto',
                  mb: 4,
                  borderRadius: '2px'
                }} 
              />
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#555',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 6
                }}
              >
                Our revolutionary REWOLUTE RO systems deliver industry-leading performance with 97% recovery rate,
                minimal brine output, and superior energy efficiency
              </Typography>
            </motion.div>
          </Box>
          
          {/* RO Systems content */}
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
              >
                <Box sx={{ 
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
                }}>
                  <Box 
                    component="img"
                    src="/images/revolute/re-1.png"
                    alt="REWOLUTE RO System"
                    sx={{ 
                      width: '100%', 
                      height: 'auto', 
                      display: 'block',
                      transition: 'transform 0.5s ease',
                      '&:hover': {
                        transform: 'scale(1.03)'
                      }
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 3,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                    color: 'white',
                    textAlign: 'left'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      REWOLUTE Single Skid System
                    </Typography>
                    <Typography variant="body2">
                      Compact design with 97% recovery rate and minimal footprint
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
              >
                <Box sx={{ 
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
                }}>
                  <Box 
                    component="img"
                    src="/images/revolute/re-2.png"
                    alt="REWOLUTE Advanced System"
                    sx={{ 
                      width: '100%', 
                      height: 'auto', 
                      display: 'block',
                      transition: 'transform 0.5s ease',
                      '&:hover': {
                        transform: 'scale(1.03)'
                      }
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 3,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                    color: 'white',
                    textAlign: 'left'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      REWOLUTE Industrial Installation
                    </Typography>
                    <Typography variant="body2">
                      Complete automation with lowest power consumption and easy monitoring
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Section>
      
      {/* CTA Section */}
      <Section>
        <Container maxWidth="md">
          <Box 
            sx={{ 
              textAlign: 'center',
              py: 6,
              px: 4,
              borderRadius: '16px',
              bgcolor: '#0ea5e9',
              color: 'white',
              boxShadow: '0 20px 40px rgba(14, 165, 233, 0.3)'
            }}
          >
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                mb: 3
              }}
            >
              Ready to Transform Your Water Treatment?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                fontSize: '1.1rem',
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              Contact our team today to learn how our patent technologies can improve your 
              water treatment processes and reduce operational costs.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                bgcolor: 'white',
                color: '#0ea5e9',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                },
                px: 4,
                py: 1.5,
                fontWeight: 600
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Section>
    </Box>
  );
}
